import { useState, useMemo, type RefObject } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Upload, Download, AlertCircle, Check, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { validatePasswordWithConfirmation, getStrengthLevel, calculatePasswordStrength } from "@/lib/password-validation";
import { isArabicOnly, isValidPhoneWithCountryCode } from "@/lib/text-validation";
import { COUNTRIES } from "@/lib/countries";
import { AccountAlreadyExistsError } from "@/api/errors";

interface RegisterFormProps {
  onToggleForm: () => void;
  redirectTo?: string;
  /** Optional ref forwarded to the first input for focus-on-transition. */
  firstInputRef?: RefObject<HTMLInputElement>;
}

export const RegisterForm = ({ onToggleForm, redirectTo, firstInputRef }: RegisterFormProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  // Basic Account Information
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [officialPhoneNumber, setOfficialPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Publisher Information
  const [publisherNameEnglish, setPublisherNameEnglish] = useState("");
  const [publisherNameArabic, setPublisherNameArabic] = useState("");
  const [legalEntityType, setLegalEntityType] = useState("");
  const [commercialRegistrationNumber, setCommercialRegistrationNumber] = useState("");
  const [countryOfRegistration, setCountryOfRegistration] = useState("");
  const [businessLicense, setBusinessLicense] = useState<File | null>(null);

  // Legal Representative Information
  const [legalRepresentativeName, setLegalRepresentativeName] = useState("");
  const [nationalIdPassportNumber, setNationalIdPassportNumber] = useState("");

  // Terms and Conditions
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [publisherNameArabicError, setPublisherNameArabicError] = useState<string | null>(null);
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const { register } = useAuth();

  // Country options for searchable select (memoized)
  // Value is country code (e.g., "sa", "ae") sent to backend
  const countryOptions = useMemo(() =>
    COUNTRIES.map((country) => ({
      value: country.code,
      label: i18n.language === 'ar' ? country.ar : country.en,
      searchAlt: `${country.ar} ${country.en}`,
    })),
    [i18n.language]
  );

  // Password validation
  const passwordValidation = useMemo(
    () => validatePasswordWithConfirmation(password, confirmPassword),
    [password, confirmPassword]
  );
  const passwordStrength = useMemo(() => calculatePasswordStrength(password), [password]);
  const strengthLevel = useMemo(() => getStrengthLevel(passwordStrength), [passwordStrength]);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: t("auth.invalidFileType"),
          description: t("auth.uploadPdfOrImage"),
          variant: "destructive",
        });
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: t("auth.fileTooLarge"),
          description: t("auth.fileMaxSize"),
          variant: "destructive",
        });
        return;
      }
      setBusinessLicense(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Trim all text inputs (except passwords which may intentionally have spaces)
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    const trimmedPhoneNumber = officialPhoneNumber.trim();
    const trimmedPublisherNameEnglish = publisherNameEnglish.trim();
    const trimmedPublisherNameArabic = publisherNameArabic.trim();
    const trimmedCommercialRegNumber = commercialRegistrationNumber.trim();
    const trimmedLegalRepName = legalRepresentativeName.trim();
    const trimmedNationalId = nationalIdPassportNumber.trim();

    // Validate basic account information
    if (!trimmedFirstName || !trimmedLastName || !trimmedEmail || !trimmedPhoneNumber || !password || !confirmPassword) {
      toast({
        title: t("auth.missingInformation"),
        description: t("auth.fillAllBasicFields"),
        variant: "destructive",
      });
      return;
    }

    // Validate phone number format - must start with country code (00 or +)
    if (!isValidPhoneWithCountryCode(trimmedPhoneNumber)) {
      setPhoneNumberError(t("auth.phoneCountryCodeRequired"));
      toast({
        title: t("auth.invalidPhoneNumber"),
        description: t("auth.phoneCountryCodeRequired"),
        variant: "destructive",
      });
      return;
    }

    // Validate publisher information
    if (!trimmedPublisherNameEnglish || !trimmedPublisherNameArabic || !legalEntityType ||
        !trimmedCommercialRegNumber || !countryOfRegistration) {
      toast({
        title: t("auth.missingPublisherInfo"),
        description: t("auth.fillAllPublisherFields"),
        variant: "destructive",
      });
      return;
    }

    // Validate Arabic publisher name contains only Arabic characters
    if (!isArabicOnly(trimmedPublisherNameArabic)) {
      setPublisherNameArabicError(t("auth.publisherNameArabicOnlyError"));
      toast({
        title: t("auth.invalidPublisherNameArabic"),
        description: t("auth.publisherNameArabicOnlyError"),
        variant: "destructive",
      });
      return;
    }

    // Validate legal representative information
    if (!trimmedLegalRepName || !trimmedNationalId) {
      toast({
        title: t("auth.missingLegalRepInfo"),
        description: t("auth.fillAllLegalRepFields"),
        variant: "destructive",
      });
      return;
    }

    // Validate terms and conditions acceptance
    if (!acceptTerms) {
      toast({
        title: t("auth.termsNotAccepted"),
        description: t("auth.pleaseAcceptTerms"),
        variant: "destructive",
      });
      return;
    }

    // Validate password requirements
    if (!passwordValidation.isValid) {
      const unmetRequirements = passwordValidation.requirements
        .filter(req => !req.met)
        .map(req => t(req.labelKey))
        .join(', ');
      toast({
        title: t("auth.weakPassword"),
        description: unmetRequirements,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      await register(
        trimmedFirstName,
        trimmedLastName,
        trimmedEmail,
        trimmedPhoneNumber,
        password,  // Password not trimmed - may intentionally have spaces
        trimmedPublisherNameEnglish,
        trimmedPublisherNameArabic,
        legalEntityType,
        trimmedCommercialRegNumber,
        countryOfRegistration,
        businessLicense,
        trimmedLegalRepName,
        trimmedNationalId
      );
      // Redirect to registration success page for email verification
      navigate('/registration-success');
    } catch (error: unknown) {
      console.error('Registration error:', error);

      if (error instanceof AccountAlreadyExistsError) {
        setErrorMessage(t("auth.accountAlreadyExists"));
      } else {
        setErrorMessage(t("auth.errorOccurred"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto shadow-lg border border-gray-200" dir={isRTL ? 'rtl' : 'ltr'}>
      <CardHeader>
        <CardTitle>{t("auth.registerTitle")}</CardTitle>
        <CardDescription>
          {t("auth.registerDescription")}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          {/* Basic Account Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t("auth.accountInformation")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.firstName")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                  ref={firstInputRef}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.lastName")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.email")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="officialPhoneNumber" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.officialPhoneNumber")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="officialPhoneNumber"
                  type="tel"
                  placeholder="+966501234567"
                  value={officialPhoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\s/g, '');
                    setOfficialPhoneNumber(value);
                    // Real-time validation
                    if (value && !isValidPhoneWithCountryCode(value)) {
                      setPhoneNumberError(t("auth.phoneCountryCodeRequired"));
                    } else {
                      setPhoneNumberError(null);
                    }
                  }}
                  disabled={isLoading}
                  required
                  dir="ltr"
                  className={phoneNumberError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {phoneNumberError ? (
                  <p className={`text-xs text-red-500 ${isRTL ? 'text-right' : ''}`}>{phoneNumberError}</p>
                ) : (
                  <p className={`text-xs text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
                    {t("auth.phoneCountryCodeHint")}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.password")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setPasswordTouched(true)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                  showPasswordLabel={t("auth.showPassword")}
                  hidePasswordLabel={t("auth.hidePassword")}
                />
                {/* Password strength indicator */}
                {password && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${
                            strengthLevel === 'weak' ? 'bg-red-500' :
                            strengthLevel === 'fair' ? 'bg-yellow-500' :
                            strengthLevel === 'good' ? 'bg-blue-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${passwordStrength}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${
                        strengthLevel === 'weak' ? 'text-red-500' :
                        strengthLevel === 'fair' ? 'text-yellow-600' :
                        strengthLevel === 'good' ? 'text-blue-500' :
                        'text-green-500'
                      }`}>
                        {t(`auth.passwordStrength.${strengthLevel}`)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.confirmPassword")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                  showPasswordLabel={t("auth.showPassword")}
                  hidePasswordLabel={t("auth.hidePassword")}
                />
              </div>
            </div>
            {/* Password requirements checklist */}
            {passwordTouched && password && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                <p className="text-sm font-medium mb-2">{t("auth.passwordRequirements")}</p>
                <ul className="space-y-1">
                  {passwordValidation.requirements.map((req) => (
                    <li key={req.code} className="flex items-center gap-2 text-sm">
                      {req.met ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <X className="h-4 w-4 text-red-500" />
                      )}
                      <span className={req.met ? 'text-green-700' : 'text-red-700'}>
                        {t(req.labelKey)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <Separator />

          {/* Publisher Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t("auth.publisherInformation")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="publisherNameEnglish" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.publisherNameEnglish")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="publisherNameEnglish"
                  type="text"
                  placeholder="ABC Publishing House"
                  value={publisherNameEnglish}
                  onChange={(e) => setPublisherNameEnglish(e.target.value)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="publisherNameArabic" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.publisherNameArabic")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="publisherNameArabic"
                  type="text"
                  placeholder="دار نشر ABC"
                  value={publisherNameArabic}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPublisherNameArabic(value);
                    // Real-time validation
                    if (value && !isArabicOnly(value)) {
                      setPublisherNameArabicError(t("auth.publisherNameArabicOnlyError"));
                    } else {
                      setPublisherNameArabicError(null);
                    }
                  }}
                  disabled={isLoading}
                  required
                  dir="rtl"
                  className={`text-right ${publisherNameArabicError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {publisherNameArabicError ? (
                  <p className="text-xs text-red-500 text-right">{publisherNameArabicError}</p>
                ) : (
                  <p className={`text-xs text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
                    {t("auth.publisherNameArabicHint")}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="legalEntityType" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.legalEntityType")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Select
                  value={legalEntityType}
                  onValueChange={setLegalEntityType}
                  disabled={isLoading}
                  required
                >
                  <SelectTrigger id="legalEntityType">
                    <SelectValue placeholder={t("auth.selectEntityType")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUBLISHING_COMPANY">{t("auth.publishingCompany")}</SelectItem>
                    <SelectItem value="GOVERNMENT_INSTITUTION">{t("auth.govInstitution")}</SelectItem>
                    <SelectItem value="NGO">{t("auth.ngo")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className={`text-xs text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
                  {t("auth.legalEntityTypeHint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="commercialRegistrationNumber" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.commercialRegistrationNumber")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="commercialRegistrationNumber"
                  type="text"
                  placeholder="1234567890"
                  value={commercialRegistrationNumber}
                  onChange={(e) => setCommercialRegistrationNumber(e.target.value)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                />
                <p className={`text-xs text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
                  {t("auth.commercialRegistrationNumberHint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="countryOfRegistration" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.countryOfRegistration")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <SearchableSelect
                  options={countryOptions}
                  value={countryOfRegistration}
                  onValueChange={setCountryOfRegistration}
                  placeholder={t("auth.selectCountry")}
                  searchPlaceholder={t("auth.searchCountry")}
                  emptyText={t("auth.noCountryFound")}
                  disabled={isLoading}
                />
              </div>
              {/* <div className="space-y-2">
                <Label htmlFor="businessLicense" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.businessLicense")} <span className="text-muted-foreground">({t("auth.optional")})</span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="businessLicense"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    disabled={isLoading}
                    className="cursor-pointer"
                  />
                  {businessLicense && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <Upload className="h-4 w-4" />
                      {businessLicense.name}
                    </span>
                  )}
                </div>
                <p className={`text-xs text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
                  {t("auth.businessLicenseHint")}
                </p>
              </div> */}
            </div>
          </div>

          <Separator />

          {/* Legal Representative Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t("auth.legalRepresentativeInformation")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="legalRepresentativeName" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.legalRepresentativeName")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="legalRepresentativeName"
                  type="text"
                  placeholder="Legal Name"
                  value={legalRepresentativeName}
                  onChange={(e) => setLegalRepresentativeName(e.target.value)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                />
                <p className={`text-xs text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
                  {t("auth.legalRepresentativeNameHint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nationalIdPassportNumber" className={isRTL ? 'text-right block' : ''}>
                  {t("auth.nationalIdPassportNumber")} <span className="text-red-500">{t("auth.required")}</span>
                </Label>
                <Input
                  id="nationalIdPassportNumber"
                  type="text"
                  placeholder="1234567890"
                  value={nationalIdPassportNumber}
                  onChange={(e) => setNationalIdPassportNumber(e.target.value)}
                  disabled={isLoading}
                  required
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Terms and Conditions */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Checkbox
                id="acceptTerms"
                checked={acceptTerms}
                onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                disabled={isLoading}
              />
              <Label htmlFor="acceptTerms" className={`cursor-pointer ${isRTL ? 'text-right' : ''}`}>
                {t("auth.acceptTermsLabel")} <span className="text-red-500">{t("auth.required")}</span>
              </Label>
              <a
                href={i18n.language === 'ar' ? '/terms/terms-and-conditions-ar.pdf' : '/terms/terms-and-conditions-en.pdf'}
                download
                className="text-sm text-primary hover:underline flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="h-4 w-4" />
                {t("auth.downloadTerms")}
              </a>
              <a
                href={i18n.language === 'ar' ? '/terms/faq-ar.pdf' : '/terms/faq-en.pdf'}
                download
                className="text-sm text-primary hover:underline flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="h-4 w-4" />
                {t("auth.downloadFaq")}
              </a>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Button type="submit" className="w-full" disabled={isLoading || !acceptTerms}>
            {isLoading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {t("auth.registerButton")}
          </Button>

          {errorMessage && (
            <Alert variant="destructive" className="w-full">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <p className="text-sm text-muted-foreground text-center">
            {t("auth.alreadyHaveAccount")}{" "}
            <button
              type="button"
              onClick={onToggleForm}
              className="text-primary hover:underline font-medium"
            >
              {t("auth.loginHere")}
            </button>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
};
